import React from "react";
import { Card, Button } from "./ui/index.js";
import { useAuth } from "../context/AuthContext.jsx";
import { formatDateLong } from "../lib/formatCop.js";

/**
 * BillingSuspendedOverlay — full-viewport non-dismissible overlay shown when
 * the API returns HTTP 402 BILLING_SUSPENDED.
 *
 * - Renders nothing when billingSuspended is null/falsy.
 * - Renders nothing for master role (defense-in-depth: backend already exempts masters).
 * - The only action available is "Cerrar sesión" which calls logout().
 */
export default function BillingSuspendedOverlay() {
  const { billingSuspended, user, logout } = useAuth();

  // Do not render if not suspended
  if (!billingSuspended) return null;

  // Master is always exempt — defense-in-depth
  if (user?.role === "master") return null;

  const billingEmail = billingSuspended.billing_email ?? null;
  const updatedAt = billingSuspended.updated_at ?? billingSuspended.suspended_at ?? null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-background-dark/90 backdrop-blur-sm flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="billing-suspended-title"
    >
      <Card className="w-full max-w-md">
        <Card.Header>
          <h2
            id="billing-suspended-title"
            className="text-text-primary text-lg font-semibold"
          >
            Cuenta suspendida por mora
          </h2>
        </Card.Header>

        <Card.Body>
          <p className="text-text-secondary text-sm mb-3">
            El acceso a la plataforma ha sido suspendido por falta de pago.
            Para restablecer el servicio, comunícate con el área de cobranza
            y regulariza tu situación.
          </p>

          {billingEmail && (
            <p className="text-text-secondary text-sm mb-3">
              Contacto de facturación:{" "}
              <span className="text-text-primary font-medium">
                {billingEmail}
              </span>
            </p>
          )}

          {updatedAt && (
            <p className="text-text-muted text-xs uppercase tracking-wide">
              Última actualización · {formatDateLong(updatedAt)}
            </p>
          )}
        </Card.Body>

        <Card.Footer>
          <Button
            variant="primary"
            size="md"
            onClick={() => logout()}
          >
            Cerrar sesión
          </Button>
        </Card.Footer>
      </Card>
    </div>
  );
}
